/**
 * Keep householdContext equal to `state.auth.householdId`, after every action.
 *
 * householdContext is a cache of the Redux value for services that cannot reach
 * the store. It used to be written only by the reducers that set the household
 * (`setHouseholdId`, `loadHouseholdId.fulfilled`), so after a reload it stayed
 * empty until the profile read came back from Firestore, while Redux already
 * held the persisted household. The recipe services read and wrote
 * `users/{uid}/…` in that window, where the household never looks.
 *
 * Mirroring the store instead of individual actions also covers whatever else
 * moves the value: the restore at boot, a different account signing in, a
 * household left on another screen.
 */
import {
  clearActiveHouseholdId,
  getActiveHouseholdId,
  setActiveHouseholdId,
} from '../services/householdContext';

interface HouseholdStore {
  getState: () => { auth?: { householdId?: string | null } };
  subscribe: (listener: () => void) => () => void;
}

export function mirrorHouseholdContext(store: HouseholdStore): () => void {
  const sync = () => {
    const householdId = store.getState().auth?.householdId ?? null;
    if (householdId === getActiveHouseholdId()) return;
    if (householdId) {
      setActiveHouseholdId(householdId);
    } else {
      clearActiveHouseholdId();
    }
  };
  sync();
  return store.subscribe(sync);
}
